import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, X, Send, Loader2, Trash2, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

export default function AdminVendorsTab() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", categories: "", payment_terms: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vendors");
      setVendors(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveVendor = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setSaving(true);
    try {
      const payload = { ...form, categories: form.categories ? form.categories.split(",").map(s => s.trim()) : [] };
      await api.post("/admin/vendors", payload);
      toast.success("Vendor added");
      setShowForm(false);
      setForm({ name: "", company: "", email: "", phone: "", categories: "", payment_terms: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await api.delete(`/admin/vendors/${id}`);
      toast.success("Vendor deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendVendorEmail = async () => {
    if (!selected || !emailForm.subject.trim() || !emailForm.message.trim()) return;
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/admin/vendors/${selected.id}/email`, emailForm);
      toast.success(`Email ${data.email_status === "sent" ? "sent" : "logged (provider not configured)"}`);
      setEmailForm({ subject: "", message: "" });
      // Reload vendor to see communication history
      const { data: updated } = await api.get(`/admin/vendors/${selected.id}`);
      setSelected(updated);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-vendors-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{vendors.length} vendor(s)</p>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-primary-foreground px-4 py-2 text-sm flex items-center gap-2"
          data-testid="vendor-add-btn"
        >
          <Plus size={14} /> Add Vendor
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={saveVendor} className="border border-border p-5 bg-card space-y-3" data-testid="vendor-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Vendor name *" className="bg-muted px-3 py-2.5 text-sm outline-none" required data-testid="vendor-name" />
            <input value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} placeholder="Company" className="bg-muted px-3 py-2.5 text-sm outline-none" data-testid="vendor-company" />
            <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="Email *" className="bg-muted px-3 py-2.5 text-sm outline-none" required data-testid="vendor-email" />
            <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="Phone" className="bg-muted px-3 py-2.5 text-sm outline-none" data-testid="vendor-phone" />
            <input value={form.categories} onChange={e => setForm(f => ({...f, categories: e.target.value}))} placeholder="Categories (comma separated)" className="bg-muted px-3 py-2.5 text-sm outline-none" data-testid="vendor-categories" />
            <input value={form.payment_terms} onChange={e => setForm(f => ({...f, payment_terms: e.target.value}))} placeholder="Payment terms" className="bg-muted px-3 py-2.5 text-sm outline-none" data-testid="vendor-terms" />
          </div>
          <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Notes" rows={2} className="w-full bg-muted px-3 py-2.5 text-sm outline-none resize-none" data-testid="vendor-notes" />
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="bg-primary text-primary-foreground px-6 py-2 text-sm disabled:opacity-50" data-testid="vendor-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="border border-border px-4 py-2 text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <div className="py-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-primary" /></div>
      ) : vendors.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <Building2 size={32} className="mx-auto mb-3 opacity-40" />
          <p>No vendors yet. Add your first vendor above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map(v => (
            <div key={v.id} className="border border-border p-4 bg-card hover:border-primary/30 cursor-pointer transition-colors" onClick={() => setSelected(v)} data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{v.name}</p>
                  {v.company && <p className="text-xs text-muted-foreground">{v.company}</p>}
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteVendor(v.id); }} className="text-muted-foreground hover:text-destructive p-1">
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{v.email}</p>
              {v.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {v.categories.map((c, i) => (
                    <span key={i} className="text-[10px] bg-secondary px-2 py-0.5 rounded">{c}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vendor Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="vendor-drawer">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-background h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-secondary/60 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-border z-10">
              <div>
                <p className="font-display text-lg text-primary">{selected.name}</p>
                {selected.company && <p className="text-xs text-muted-foreground">{selected.company}</p>}
              </div>
              <button onClick={() => setSelected(null)}><X size={22} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs uppercase text-muted-foreground">Email</p><a href={`mailto:${selected.email}`} className="text-primary hover:underline">{selected.email}</a></div>
                <div><p className="text-xs uppercase text-muted-foreground">Phone</p><p>{selected.phone || "-"}</p></div>
                <div><p className="text-xs uppercase text-muted-foreground">Payment Terms</p><p>{selected.payment_terms || "-"}</p></div>
              </div>
              {selected.notes && <div><p className="text-xs uppercase text-muted-foreground mb-1">Notes</p><p className="text-sm">{selected.notes}</p></div>}

              {/* Send Email */}
              <div className="border-t border-border pt-4">
                <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-3 flex items-center gap-2"><Mail size={14} /> Send Email</p>
                <input
                  value={emailForm.subject}
                  onChange={e => setEmailForm(f => ({...f, subject: e.target.value}))}
                  placeholder="Subject"
                  className="w-full bg-muted px-3 py-2.5 text-sm outline-none mb-2"
                  data-testid="vendor-email-subject"
                />
                <textarea
                  value={emailForm.message}
                  onChange={e => setEmailForm(f => ({...f, message: e.target.value}))}
                  placeholder="Message..."
                  rows={3}
                  className="w-full bg-muted px-3 py-2.5 text-sm outline-none resize-none mb-2"
                  data-testid="vendor-email-message"
                />
                <button
                  onClick={sendVendorEmail}
                  disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.message.trim()}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="vendor-email-send"
                >
                  {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                </button>
              </div>

              {/* Communication History */}
              {selected.communication_history?.length > 0 && (
                <div className="border-t border-border pt-4">
                  <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-3">History</p>
                  <div className="space-y-2">
                    {selected.communication_history.slice().reverse().map((h) => (
                      <div key={h.id} className="bg-muted p-3 text-sm">
                        <p className="font-medium text-xs">{h.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">{h.sent_by} &middot; {new Date(h.created_at).toLocaleDateString("en-IN")} &middot; {h.email_status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
